import { useRef, useState } from 'react';

import { useUploadDocument } from '../../../conversations/hooks/use-upload-document';
import { ConflictAlertSeverityBadge } from '../badges/ConflictAlertSeverityBadge';
import { ConflictAlertTypeBadge } from '../badges/ConflictAlertTypeBadge';

import { ModalBase } from './ModalBase';

import type { EvidenceDoc } from './MitigateAlertModal';
import type { ConflictSeverityVM, SupervisorVariant } from '../../view-models';

export interface AcceptAlertWithJustificationModalProps {
  open: boolean;
  alert: {
    id: string;
    severity: ConflictSeverityVM;
    alertType: string;
    chain: { position: number; total: number };
    dualApprovalRequired?: boolean;
  } | null;
  variant: SupervisorVariant;
  /** Bond do alerta (`alert.bondsInvolved[0]`); `undefined` desabilita o anexo. */
  bondId: string | undefined;
  /** Mutation em voo — desabilita o submit p/ evitar duplo-POST (review T7). */
  submitting?: boolean;
  onClose: () => void;
  onConfirm: (p: {
    justif: string;
    acceptType: 'temp' | 'perm';
    ttlDays: number;
    evidence: EvidenceDoc[];
    category: string;
    dualApproval: boolean;
  }) => void;
}

// Convertido verbatim de design-references/brief-3/shared/modals-3.jsx:225-340 (§12.7).
export function AcceptAlertWithJustificationModal({
  open,
  alert,
  variant,
  bondId,
  submitting = false,
  onClose,
  onConfirm,
}: AcceptAlertWithJustificationModalProps): React.JSX.Element | null {
  const [justif, setJustif] = useState('');
  const [acceptType, setAcceptType] = useState<'temp' | 'perm'>('temp');
  const [ttlDays, setTtlDays] = useState(30);
  const [evidence, setEvidence] = useState<EvidenceDoc[]>([]);
  const [category, setCategory] = useState('familiar');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading } = useUploadDocument(bondId);
  if (!open || alert === null) return null;

  const isDualApproval = alert.dualApprovalRequired === true || alert.chain.position >= 3;
  const docRequired = variant === 'regulated';
  const canAttach = bondId !== undefined;
  const canSubmit =
    justif.trim().length >= 100 &&
    (!docRequired || evidence.length > 0) &&
    !isUploading &&
    !submitting;

  const onPickFile = async (file: File): Promise<void> => {
    setUploadError(null);
    try {
      const doc = await upload(file);
      setEvidence((e) => [...e, { id: doc.id, name: doc.name }]);
    } catch (err) {
      // Não engolir a rejeição — documentação é obrigatória em Regulated (review T7).
      setUploadError(err instanceof Error ? err.message : 'Falha ao enviar o arquivo.');
    }
  };

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Aceitar alerta com justificativa"
      width={640}
      footer={
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-3)' }}>
            {isDualApproval && '⚠ aprovação DUPLA: supervisor + gestor'}
            {!isDualApproval &&
              docRequired &&
              (evidence.length === 0
                ? '⚠ Regulated: documentação OBRIGATÓRIA'
                : '✓ documentação anexada')}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-ghost-mini" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-ghost-mini btn-ghost-mini--primary"
              disabled={!canSubmit}
              onClick={() =>
                onConfirm({
                  justif,
                  acceptType,
                  ttlDays,
                  evidence,
                  category,
                  dualApproval: isDualApproval,
                })
              }
            >
              {isDualApproval ? 'Enviar para aprovação dupla' : 'Confirmar aceitação'}
            </button>
          </span>
        </div>
      }
    >
      <div className="modal-form">
        <div className="modal-form__alert-head">
          <ConflictAlertSeverityBadge severity={alert.severity} />
          <ConflictAlertTypeBadge alertType={alert.alertType} />
        </div>

        <div className="modal-form__callout modal-form__callout--warn">
          ⚠ <b>Aceitar</b> significa que o alert é VÁLIDO mas você reconhece o contexto como
          aceitável. Decisão registrada permanentemente.
        </div>
        {isDualApproval && (
          <div className="modal-form__callout modal-form__callout--danger">
            ⚠ Alert critical em chain pos {alert.chain.position}/{alert.chain.total} — aceitação
            requer
            <b> aprovação DUPLA</b> (supervisor + gestor).
          </div>
        )}

        <fieldset className="modal-form__fieldset">
          <legend>Categoria do conflito</legend>
          {[
            { id: 'familiar', label: 'Relação familiar declarada' },
            { id: 'profissional', label: 'Relação profissional pré-existente' },
            { id: 'identidade', label: 'Compartilhamento de identidade técnica (CPF/email)' },
            { id: 'outro', label: 'Outro' },
          ].map((c) => (
            <label key={c.id} className="modal-form__radio">
              <input
                type="radio"
                name="cat"
                value={c.id}
                checked={category === c.id}
                onChange={() => setCategory(c.id)}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </fieldset>

        <label className="modal-form__field">
          <span className="modal-form__label">Justificativa detalhada (mínimo 100 caracteres)</span>
          <textarea
            className="modal-form__textarea"
            value={justif}
            onChange={(e) => setJustif(e.target.value)}
            placeholder="Pedro Mendes e Maria Silva são irmãos. O número 9XXX-1234 é telefone fixo da família compartilhado quando ele atende o telefone da casa. Pedro NÃO atende casos comerciais da Maria; apenas casos onde família apoia uns aos outros sem envolvimento profissional. Aceito o alert como contexto aceitável dada a relação familiar declarada."
            rows={5}
          />
          <span className="modal-form__hint">{justif.trim().length}/100 mínimo</span>
        </label>

        <fieldset className="modal-form__fieldset">
          <legend>Tipo de aceitação</legend>
          <label className="modal-form__radio">
            <input
              type="radio"
              checked={acceptType === 'temp'}
              onChange={() => setAcceptType('temp')}
            />
            <span>Temporário (recomendado em chain) · re-avaliação automática após período</span>
          </label>
          <label className="modal-form__radio">
            <input
              type="radio"
              checked={acceptType === 'perm'}
              onChange={() => setAcceptType('perm')}
            />
            <span>Permanente (válido até revogação)</span>
          </label>
          {acceptType === 'temp' && (
            <label className="modal-form__field" style={{ marginTop: 8 }}>
              <span className="modal-form__label">Período</span>
              <select
                className="modal-form__select"
                value={ttlDays}
                onChange={(e) => setTtlDays(parseInt(e.target.value))}
              >
                <option value={7}>7 dias</option>
                <option value={15}>15 dias</option>
                <option value={30}>30 dias</option>
                <option value={60}>60 dias</option>
                <option value={90}>90 dias</option>
              </select>
            </label>
          )}
        </fieldset>

        <label className="modal-form__field">
          <span className="modal-form__label">
            Documentação{' '}
            {docRequired ? (
              <b style={{ color: 'var(--danger-fg)' }}>obrigatória (DocumentationGated)</b>
            ) : (
              '(opcional)'
            )}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            data-testid="accept-file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file !== undefined) void onPickFile(file);
            }}
          />
          <button
            type="button"
            className="btn-ghost-mini modal-form__file"
            disabled={!canAttach || isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? 'Enviando…' : '+ Adicionar arquivo'}
          </button>
          {!canAttach && (
            <span className="modal-form__hint">anexo requer bond associado ao alerta</span>
          )}
          {uploadError !== null && (
            <span className="modal-form__hint" role="alert" style={{ color: 'var(--danger-fg)' }}>
              {uploadError}
            </span>
          )}
          {evidence.length > 0 && (
            <ul className="modal-form__file-list mono">
              {evidence.map((f, i) => (
                <li key={f.id}>
                  <span>📎 {f.name}</span>
                  <button
                    type="button"
                    className="btn-ghost-mini"
                    onClick={() => setEvidence((e) => e.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>
      </div>
    </ModalBase>
  );
}
